'use client';

import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { Stack } from '@mui/system';
import { isEmpty } from 'lodash';
import { useQueryState } from 'nuqs';
import React, { forwardRef, useCallback } from 'react';
import { useSendParticipation } from 'src/api/connect';
import { useAuthContext } from 'src/auth/hooks';
import { useBoolean } from 'src/hooks/use-boolean';
import { useTranslate } from 'src/locales';
import { useParams, useRouter } from 'src/routes/hooks';
import { EventType, IEvent } from 'src/types';

import { CreateProfileDialog } from '../create-profile';
import EventDetailsContent from '../event-details-content';
import EventDetailsHeader from '../event-details-header';

type Props = {
  event: IEvent;
};

const EventDetailsMobileView = forwardRef<HTMLDivElement, Props>(({ event }, ref) => {
  const router = useRouter();
  const { id } = useParams();
  const { authenticated } = useAuthContext();
  const showCreateProfileDialog = useBoolean();
  const eventDate = new Date(event ? (event?.endDate ?? event?.date) : '');
  const { sendParticipation, loading } = useSendParticipation();
  const { t: tConnect } = useTranslate('connect'); // Destructure the t function for translations
  const [_, setOpenLogin] = useQueryState('login');
  const [___, setIsExclusiveEvent] = useQueryState('exclusive');
  const [____, setReturnTo] = useQueryState('returnTo');

  const handleClickOnConnect = useCallback(() => {
    if (!authenticated) {
      setOpenLogin('open');
      setReturnTo(`https://trybe.fm/event/${id}/attendees`);

      return null;
    }
    if (event?.type === EventType.EXCLUSIVE && isEmpty(event?.tickets)) {
      setIsExclusiveEvent('open');
      return null;
    }
    sendParticipation(id as string, { connect: true });

    router.push(`attendees`);

    return null;
  }, [
    authenticated,
    event?.type,
    event?.tickets,
    sendParticipation,
    id,
    router,
    setOpenLogin,
    setReturnTo,
    setIsExclusiveEvent,
  ]);

  return (
    <Container sx={{ p: 0 }} ref={ref}>
      {event && <EventDetailsHeader event={event} />}
      {event && <EventDetailsContent event={event} />}
      {eventDate > new Date() && (
        <Stack
          alignItems="center"
          sx={{
            position: 'fixed',
            bottom: '7%',
            flexGrow: 1,
            width: '100%',
            zIndex: 99,
          }}
        >
          <LoadingButton
            loading={loading}
            variant="contained"
            aria-label="scroll back to top"
            sx={{
              borderRadius: 1,
              p: 1.3,
              width: 'fit-content',
            }}
            onClick={() => handleClickOnConnect()}
          >
            {tConnect('buttons.connect-with-people')}
            <Box
              component="img"
              alt="logo"
              src="/assets/icons/connect/connect-icon.svg"
              sx={{ width: 20, height: 20, ml: 1 }}
            />
          </LoadingButton>
        </Stack>
      )}
      <CreateProfileDialog
        open={showCreateProfileDialog.value}
        onClose={() => showCreateProfileDialog.onFalse()}
      />
    </Container>
  );
});

export default EventDetailsMobileView;
